// The League chip's groups, shared by the live population widgets (Ranking, Filter, Player
// Comparison, Similarity) — Ben, 2026-10-16: the canvas player's league by default; tick one
// or more others to put those leagues together (each is one Stats file). Not the whole world
// (every league's file at once is too big for men), so "Not <league>" isn't offered. Live only.

import type { StatsFile } from "@catbento/shared";
import type { PlayerId } from "../data/players.js";
import { isExcludeValue, selectedValuesOf, type CategoricalActive, type KitPlayer } from "../engines/stats-filter-kit.js";
import { liveApi } from "../live/api.js";
import { useMemo } from "react";
import { useLiveQueries, useLiveQuery } from "../live/query.js";
import { loadFileLeagues, loadFileSeasons, seasonLabel } from "../live/stats-files.js";
import {
  groupChipOptions,
  groupPlayers,
  loadGroupFile,
  type GroupFile,
  type GroupPick,
  type GroupPlayer,
} from "../live/group-players.js";
import { useLiveGroupFile, type LiveGroupFile } from "./useGroupFile.js";
import { useLiveLeagueChoices } from "./useLeagueTable.js";
import { topLeagueOf } from "../live/gender-switch.js";

/** A league's season matching `target` (Q-006): the same id, else the same start year in the
 *  other format — "5YYYY" for a European season, "YYYY" for a calendar-year league (25/26 ↔
 *  NWSL 2025). Null = that league hasn't built it. Pure. */
export function matchingSeason(target: number, built: readonly number[]): number | null {
  const year = target >= 50000 ? target - 50000 : target;
  for (const c of [target, 50000 + year, year]) if (built.includes(c)) return c;
  return null;
}

/** Every league with a Stats file → its built seasons (one call per page, shared). */
export function useLeaguesWithFiles(): Map<number, number[]> {
  const res = useLiveQuery("files:leagues", () => loadFileLeagues(liveApi()))!;
  return useMemo(() => new Map(res.leagues.map((l) => [l.competitionId, l.seasonIds] as const)), [res]);
}

/** The League chip's list (Ben, 2026-10-18: "if they don't contain data … I don't want to see
 *  them"): only leagues with a Stats file for one of `seasons` (or its match in that league's own
 *  season format, Q-006). A league in `keep` (ticked, or the player's own) always stays, so a pick
 *  never vanishes from its own list. Pure. */
export function leaguesWithData<T extends { id: number; label: string }>(
  choices: readonly T[],
  built: ReadonlyMap<number, readonly number[]>,
  seasons: readonly number[],
  keep: readonly (string | null)[],
): T[] {
  return choices.filter((c) => keep.includes(c.label) || seasons.some((s) => matchingSeason(s, built.get(c.id) ?? []) !== null));
}

export interface LeagueGroups {
  /** The canvas player's own league-season (its file, the default league, her club now). */
  home: LiveGroupFile;
  /** The files to read, one per ticked league with data this season. */
  groups: { group: GroupFile; label: string }[];
  /** The season every player is read on (another league's own season is re-labelled to it). */
  shown: string;
  /** The season picker: the shown league's built seasons (+ the shown one if it isn't built). */
  seasonCodes: string[];
  /** The League chip's default (the canvas player's league) and its list (her gender's leagues). */
  defaultLeague: string | undefined;
  leagueOptions: string[];
  /** Those leagues with their country, for the list's flags + country groups. */
  leagues: { label: string; countryName: string | null; countryCode: string | null; womens: boolean | null; youth: boolean | null }[];
  womens: boolean | null;
  /** The canvas player's own gender (their league's file); `womens` differs once the module's
   *  Men's / Women's switch is flipped. Null = not known (no file). */
  playerWomens: boolean | null;
  /** Why nothing can be shown, or null. Without a prefix: the widget says "No ranking: …". */
  problem: string | null;
  /** Picked leagues with no file this season (the others still show), or null. */
  missingNote: string | null;
  /** The League chip was removed (✕): no leagues, the widget asks for one (Ben, 2026-10-16). */
  cleared: boolean;
  /** Any league's chip label by its competition id (the Ranking's search finds a player's league). */
  leagueOf: (competitionId: number) => string | undefined;
  /** A league's gender by its chip label (the Ranking's search flips the switch to it); null = unknown. */
  womensOfLeague: (label: string) => boolean | null;
}

/** The groups for the League chip's value (`league`), around `playerId` in `season`. */
export function useLiveLeagueGroups(
  playerId: PlayerId,
  season: string | null,
  league: CategoricalActive | undefined,
  /** The user removed the League chip (useLeagueChip `cleared`): empty, not the default. */
  cleared = false,
  /** The Men's / Women's switch (Ranking, Filter, Stats Table): the gender shown when it isn't the
   *  player's. Undefined / null = the player's. */
  womensPick: boolean | null = null,
): LeagueGroups {
  const home = useLiveGroupFile(playerId, season);
  const homeGroup = home.kind === "file" ? home.group : null;
  const playerWomens = homeGroup ? homeGroup.file.womens : null;
  // Switched to the other gender: the player's league is out of reach (never a mixed group), and
  // the other gender's top league is the default instead.
  const switched = womensPick !== null && playerWomens !== null && womensPick !== playerWomens;
  const homeLabel = home.kind === "file" && !switched ? home.league.label : null;

  const choices = useLiveLeagueChoices();
  const withFiles = useLeaguesWithFiles();
  const otherTop = switched ? topLeagueOf(womensPick, choices, withFiles) : null;
  const excluded = league !== undefined && isExcludeValue(league);
  const ticked = selectedValuesOf(league);
  // Empty: the chip was removed (✕), or added back with nothing ticked yet.
  const empty = (cleared || Array.isArray(league)) && ticked.length === 0;
  const labels = ticked.length > 0 ? ticked : homeLabel === null || empty ? [] : [homeLabel];
  const withHome = homeLabel !== null && labels.includes(homeLabel);
  const otherLabels = labels.filter((l) => l !== homeLabel);
  const unknown = otherLabels.filter((l) => !choices.some((c) => c.label === l));
  const others = choices.filter((c) => otherLabels.includes(c.label));
  const builtLists = useLiveQueries(
    others.map((c) => ({ key: `files:seasons:${c.id}`, load: () => loadFileSeasons(liveApi(), c.id) })),
  );
  const target = season ? Number(season) : home.seasonId;
  const otherSeasons = others.map((_, i) => matchingSeason(target, builtLists[i]!.seasons.map((x) => x.seasonId)));
  const ready = others.flatMap((c, i) => (otherSeasons[i] === null ? [] : [{ league: c, seasonId: otherSeasons[i]! }]));
  const notBuilt = others.filter((_, i) => otherSeasons[i] === null).map((c) => c.label);
  const readyGroups = useLiveQueries(
    ready.map((r) => ({
      key: `group:file:${r.league.id}:${r.seasonId}`,
      load: async () => {
        const f = await loadGroupFile(liveApi(), r.league.id, r.seasonId);
        if (f === null) throw new Error(`Stats file ${r.league.id}/${r.seasonId} is listed as built, but isn't there`);
        return f;
      },
    })),
  );
  const groups = [
    ...(withHome && homeGroup && homeLabel !== null ? [{ group: homeGroup, label: homeLabel }] : []),
    ...ready.map((r, i) => ({ group: readyGroups[i]!, label: r.league.label })),
  ];
  const shown = String(withHome ? home.seasonId : (ready[0]?.seasonId ?? target));
  const built = (withHome ? home.seasons : (builtLists[0]?.seasons.map((x) => x.seasonId) ?? [])).map(String);
  const womens = switched ? womensPick : playerWomens;
  // Her gender's leagues with data this season (and whatever's ticked).
  const listed = leaguesWithData(
    choices.filter((c) => c.womens === womens),
    withFiles,
    [target],
    [...ticked, homeLabel],
  );

  let problem: string | null = null;
  if (empty) problem = "pick a league";
  else if (excluded) problem = "every league except one isn't available yet: pick a league";
  else if (unknown.length > 0) problem = `${unknown.join(", ")} isn't a league with data`;
  else if (groups.length === 0) {
    if (notBuilt.length > 0) problem = `${notBuilt.join(", ")} ${seasonLabel(target)} isn't built`;
    else if (home.kind === "none") problem = home.label;
  }
  return {
    home,
    groups,
    shown,
    seasonCodes: built.includes(shown) ? built : [shown, ...built],
    defaultLeague: homeLabel ?? otherTop ?? undefined,
    leagueOptions: listed.map((c) => c.label),
    leagues: listed,
    womens,
    playerWomens,
    problem,
    missingNote: notBuilt.length > 0 && groups.length > 0 ? `no ${seasonLabel(target)} data: ${notBuilt.join(", ")}` : null,
    cleared: empty,
    leagueOf: (id) => choices.find((c) => c.id === id)?.label,
    womensOfLeague: (label) => choices.find((c) => c.label === label)?.womens ?? null,
  };
}

/** A stable key for `groups` (memo dependency). */
export const groupsKey = (lg: LeagueGroups) => lg.groups.map((x) => `${x.label}@${x.group.file.seasonId}`).join("|");

/** Every picked league's players, read on one season code (another league's own season is
 *  re-labelled, so Ben's engines read them all on `shown`). One league keeps its own keys. */
export function playersOfGroups(lg: LeagueGroups, pick: GroupPick, keys: string[]): Record<string, GroupPlayer> {
  const players: Record<string, GroupPlayer> = {};
  const many = lg.groups.length > 1;
  for (const { group, label } of lg.groups) {
    for (const [key, p] of Object.entries(groupPlayers(group, pick, keys, label))) {
      players[many ? `${label}|${key}` : key] = { ...p, currentSeason: lg.shown, seasons: { [lg.shown]: { stats: p.stats } } };
    }
  }
  return players;
}

/** The chip values across the picked leagues, plus the League chip's list. */
export function chipOptionsOfGroups(lg: LeagueGroups): Record<string, string[]> {
  const merged: Record<string, string[]> = {};
  for (const { group } of lg.groups) {
    for (const [k, vs] of Object.entries(groupChipOptions(group))) {
      merged[k] = [...new Set([...(merged[k] ?? []), ...vs])].sort();
    }
  }
  merged.league = lg.leagueOptions;
  return merged;
}

/** The files behind the picked leagues (the 50% chip rule reads them). */
export const filesOfGroups = (lg: LeagueGroups): StatsFile[] => lg.groups.map((x) => x.group.file);

/** Her own row in her league (the chips' first values: main position, club, country); not there
 *  (another season, or she left) → her club now. */
export function refPlayerOf(lg: LeagueGroups, playerId: PlayerId): KitPlayer {
  const h = lg.home;
  if (h.kind !== "file") return {};
  return groupPlayers(h.group, "total", [], h.league.label)[String(playerId)] ?? { team: h.league.teamName };
}
